import { FileTextOutlined } from '@ant-design/icons'
import { useAirportOperationsRpt } from '@services/report'
import { Button, Input } from '@shared/Form'
import {
  IconAirplaneCircle,
  IconCarCircle,
  IconMoneyCircle,
} from '@shared/icons'
import {
  useMobilePageHeader,
  useMobilePageHeaderSetter,
} from '@shared/innmaxLib/hooks/useMobilePageHeader'
import useModal from '@shared/innmaxLib/hooks/useModal'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import { MobileRangePicker } from '@shared/innmaxUI/Form/DatePicker'
import MobileTable from '@shared/innmaxUI/MobileTable'
import Layout from '@shared/Layout'
import { toThousandSeparator } from '@shared/utils/webHelper'
import { Form } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from 'src/components/Form'
import MobilePageDrawer from 'src/components/MobilePageDrawer'
import OptionsSelect from 'src/components/OptionsSelect'
import DetailPage from './DetailPage'
import SummaryPage from './SummaryPage'

/**
 * summary: 依地勤公司分組的總覽資料
 * rawData: 依指定地勤公司車牌分組的詳細資料
 */
export type IQueryMode = 'summary' | 'rawData'

const initialReportType = {
  startAt: moment().startOf('month').valueOf(),
  endAt: moment().endOf('month').valueOf(),
}

function Operations() {
  const { t } = useTranslation()
  const { toggleActionKey } = useMobilePageHeader()
  const { setActionNodes, setEnableFilter, setToogleKey } =
    useMobilePageHeaderSetter()

  React.useLayoutEffect(() => {
    setActionNodes([
      {
        key: 'summary',
        element: <FileTextOutlined className="text-primary text-xl" />,
      },
    ])
    setEnableFilter(true)
  }, [])

  const {
    visible: mobileFilterVisible,
    open: openMobileFilter,
    close: closeMobileFilter,
  } = useModal()

  const {
    visible: visibleSummaryPage,
    open: openSummaryPage,
    close: closeSummaryPage,
  } = useModal()

  React.useEffect(() => {
    if (toggleActionKey === 'filter') {
      openMobileFilter()
    } else {
      closeMobileFilter()
    }
    if (toggleActionKey === 'summary') {
      openSummaryPage()
    } else {
      closeSummaryPage()
    }
  }, [toggleActionKey])

  const {
    item: detailItem,
    visible: mobileDetailPageVisible,
    open: openMobileDetailPage,
    close: closeMobileDetailPage,
  } = useModal()

  const [form] = Form.useForm()

  const prevQueryRef = React.useRef<any>({})

  const { query, handleSearch, handleTableChange, isTriggerSearch } = useSearch(
    {
      form,
      initial: { ...initialReportType },
      dateRangeField: ['startAt', 'endAt'],
    }
  )

  React.useEffect(() => {
    if (!isTriggerSearch) {
      return
    }
    const { reactQueryCache: prevCache, ...prevRest } = prevQueryRef.current
    const { reactQueryCache: currentCache, ...currentRest } = query
    const isEqual = JSON.stringify(prevRest) === JSON.stringify(currentRest)
    if (!isEqual) {
      //   refetchSummary(query)
    }
    prevQueryRef.current = query
  }, [query, isTriggerSearch])

  const { dataSource, loading } = useAirportOperationsRpt(query, {
    enabled: isTriggerSearch,
  })

  const _queryMode: IQueryMode = query.orgSessionId ? 'rawData' : 'summary'

  return (
    <>
      <Layout>
        <MobilePageDrawer
          open={mobileFilterVisible}
          title="搜尋"
          onClose={() => {
            closeMobileFilter()
            setToogleKey('')
          }}>
          <Form layout="inline" form={form}>
            <FormItem
              label={'關鍵字'}
              name="keyword"
              tooltip="支援車牌,EVCCID,設備名稱">
              <Input placeholder="車牌,EVCCID,設備名稱" />
            </FormItem>
            <FormItem label={'地勤公司'} name="orgSessionId">
              <OptionsSelect.CompaniesSelect />
            </FormItem>
            <FormItem
              label={'時間範圍'}
              name="date"
              initialValue={[
                moment(initialReportType.startAt),
                moment(initialReportType.endAt),
              ]}>
              <MobileRangePicker />
            </FormItem>

            <FormItem.Button>
              <Button
                loading={loading}
                type="primary"
                className="w-96"
                onClick={() => {
                  closeMobileFilter()
                  setToogleKey('')
                  handleSearch()
                }}>
                {t('common:search')}
              </Button>
            </FormItem.Button>
          </Form>
        </MobilePageDrawer>
        <DetailPage
          queryMode={_queryMode}
          open={mobileDetailPageVisible}
          data={detailItem}
          onClose={closeMobileDetailPage}
        />
        <SummaryPage
          queryMode={_queryMode}
          open={visibleSummaryPage}
          onClose={() => {
            closeSummaryPage()
            setToogleKey('')
          }}
          query={query}
          isTriggerSearch={isTriggerSearch}
        />

        <MobileTable
          dataSource={dataSource}
          onRowClick={record => {
            openMobileDetailPage(record)
          }}
          onMobileRowRender={record => (
            <div className="flex justify-evenly gap-10">
              {query.orgSessionId ? (
                <div className="flex gap-5 mb-2 flex-1 text-white font-bold items-center">
                  <div className="text-primary">
                    <IconCarCircle className="w-16 h-16" />
                  </div>

                  {record.lprNumber}
                </div>
              ) : (
                <div className="flex gap-5 mb-2 flex-1 text-white font-bold ">
                  <div className="text-primary">
                    <IconAirplaneCircle />
                  </div>
                  {record.orgSessionName}
                </div>
              )}
              <div className="flex gap-5 flex-1 font-bold text-primary items-center">
                <IconMoneyCircle />
                {toThousandSeparator(record.totalAmount)}
              </div>
            </div>
          )}
        />
      </Layout>
    </>
  )
}

export default React.memo(Operations)
